/**
 * Folder organization configuration for saved queries.
 *
 * Defines folder categories (AD, Cloud, ATO, Security Advisories) and maps
 * default queries to their appropriate folders. Custom user queries default
 * to the CUSTOM_QUERIES folder.
 *
 * @module folderConfig
 */

import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CloudIcon,
  FolderIcon,
  FolderSearchIcon,
  ShieldCheckIcon,
  UserIcon,
} from 'lucide-react';
import type { ComponentType } from 'react';

/**
 * Query folder type constants.
 * Used for static categorization of saved queries by their ID prefix or source.
 */
export const QueryFolders = {
  AD_ATTACK_PATHS: 'ad-attack-paths',
  CLOUD_ATTACK_PATHS: 'cloud-attack-paths',
  ATO_ATTACK_PATHS: 'ato-attack-paths',
  SECURITY_ADVISORIES: 'security-advisories',
  CUSTOM_QUERIES: 'custom-queries',
  CONCERNING_EXPOSURES: 'concerning-exposures',
  COMMON_QUERIES: 'common-queries',
} as const;

export type QueryFolderType = (typeof QueryFolders)[keyof typeof QueryFolders];

export interface QueryFolderData {
  id: QueryFolderType;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  isSystem: boolean;
  /** If true, folder is disabled at global query builder level (attack paths require single-tenant context) */
  isAttackPathFolder?: boolean;
}

/**
 * Folder configuration for organizing saved queries.
 *
 * Array order determines display order in sidebar:
 * - Custom Queries first (user-created queries)
 * - Followed by system folders (AD, Cloud, ATO, Security)
 */
export const QUERY_FOLDERS: QueryFolderData[] = [
  {
    id: QueryFolders.CUSTOM_QUERIES,
    label: 'Custom Queries',
    description: 'User-created queries',
    icon: FolderIcon,
    isSystem: false,
  },
  {
    id: QueryFolders.COMMON_QUERIES,
    label: 'Common Queries',
    description: 'Frequently used queries for common security scenarios',
    icon: FolderSearchIcon,
    isSystem: true,
  },
  {
    id: QueryFolders.CONCERNING_EXPOSURES,
    label: 'Concerning Exposures',
    description: 'Frequently used queries available to all users',
    icon: AlertTriangleIcon,
    isSystem: true,
  },
  {
    id: QueryFolders.SECURITY_ADVISORIES,
    label: 'Critical Advisories',
    description:
      'Pre-built queries for critical CVEs and security vulnerabilities',
    icon: AlertCircleIcon,
    isSystem: true,
  },
  {
    id: QueryFolders.AD_ATTACK_PATHS,
    label: 'AD Attack Paths',
    description:
      'Active Directory privilege escalation and attack path queries',
    icon: ShieldCheckIcon,
    isSystem: true,
    isAttackPathFolder: true,
  },
  {
    id: QueryFolders.CLOUD_ATTACK_PATHS,
    label: 'Cloud Attack Paths',
    description: 'Cloud infrastructure and service attack path queries',
    icon: CloudIcon,
    isSystem: true,
    isAttackPathFolder: true,
  },
  {
    id: QueryFolders.ATO_ATTACK_PATHS,
    label: 'ATO Attack Paths',
    description: 'Account takeover and identity-based attack path queries',
    icon: UserIcon,
    isSystem: true,
    isAttackPathFolder: true,
  },
];
